import React from 'react'
import undefsafe from 'undefsafe'
import raf from 'raf'

import ItemActions from './common/itemActions'
import TrendingSash from './common/trendingSash'
import VideoLoader from '../videoLoader'

import styles from './gridVideo.module.css'

class ItemCardGridVideo extends React.Component {
  constructor (props) {
    super(props)

    this.state = {
      playing: false,
      hideCursor: false,
      videoPlayPromise: null,
      videoLooped: false,
      reportedEvents: []
    }

    this.onMouseOver = this.onMouseOver.bind(this)
    this.onMouseOut = this.onMouseOut.bind(this)

    this.onMouseOverVideo = this.onMouseOverVideo.bind(this)
    this.onMouseOutVideo = this.onMouseOutVideo.bind(this)
    this.onMouseMoveVideo = this.onMouseMoveVideo.bind(this)

    this.setVideoElementRef = this.setVideoElementRef.bind(this)
    this.onVideoProgress = this.onVideoProgress.bind(this)
  }

  setVideoElementRef (element) {
    this.videoElement = element
  }

  onMouseMoveVideo () {
    if (this.state.hideCursor) {
      this.setState({ hideCursor: false })
    }
  }

  onMouseOverVideo () {
    this.timeout = setTimeout(() => {
      this.setState({ hideCursor: true })
    }, 3000)
  }

  onMouseOutVideo () {
    if (this.timeout) {
      clearTimeout(this.timeout)
    }

    this.setState({ hideCursor: false })
  }

  onVideoProgress () {
    if (this.state.playing) {
      const playProgress = Math.floor(this.videoElement.currentTime / this.videoElement.duration * 100)
      this.handleVideoReportEvent(playProgress)
      raf(this.onVideoProgress)
    }
  }

  handleVideoReportEvent (playProgress) {
    if (this.videoElement.currentTime === 0) {
      this.reportEvent('video_start')
    }

    if (playProgress >= 25 && playProgress < 50) {
      this.reportEvent('25_percent')
    }

    if (playProgress >= 50 && playProgress < 75) {
      this.reportEvent('50_percent')
    }

    if (playProgress >= 75) {
      this.reportEvent('75_percent')
    }

    if (playProgress + 1 >= 100) {
      this.reportEvent('video_complete')
      this.setState({
        videoLooped: true
      })
    }
  }

  hasEventReported (eventName) {
    return this.state.reportedEvents.indexOf(eventName) >= 0
  }

  reportEvent (eventName) {
    if (!this.state.videoLooped && !this.hasEventReported(eventName)) {
      const { item } = this.props
      const videoSrc = undefsafe(item, 'previews.icon_with_video_preview.video_url')

      if (this.props.reportVideoProgress) {
        this.props.reportVideoProgress(eventName, videoSrc)
        this.setState({
          reportedEvents: [eventName, ...this.state.reportedEvents]
        })
      }
    }
  }

  scheduleVideoReset () {
    const { videoPlayPromise } = this.state

    if (this.videoElement && videoPlayPromise) {
      this.videoResetTimeout = setTimeout(() => {
        // Resets the media element and restarts the media resource. Any pending events are discarded.
        // https://dev.w3.org/html5/spec-preview/media-elements.html#dom-media-load
        videoPlayPromise.then(() => {
          if (this.videoElement) {
            this.videoElement.load()
          }
        })
      }, 5000)
    }
  }

  discardVideoReset () {
    if (this.videoResetTimeout) {
      clearTimeout(this.videoResetTimeout)
      this.videoResetTimeout = null
    }
  }

  videoApiPlay () {
    /*
     For some old browsers (Safari 9) play() does not return promise but undefined.
     Let's handle that case by returning resolved promise instead.
     */
    return this.videoElement.play() || Promise.resolve()
  }

  playVideo () {
    if (this.videoElement && this.videoElement.paused) {
      this.setState({
        videoPlayPromise: this.videoApiPlay().then(() => {
          this.setState({ playing: true }, () => {
            this.onVideoProgress()
          })
        }).catch(error => {
          // Throw error only if HTMLMediaElement still exists, if it's unmounted let's ignored it.
          // HTMLMediaElement internally call pause when removed from DOM. If media is still loading when unmounted
          // it caused an error "play() interrupted by pause()". There is no way to wait for media to play in the process of
          // unmounting
          if (this.videoElement) {
            throw error
          }
        })
      })
    }
  }

  pauseVideo () {
    const { videoPlayPromise } = this.state

    if (videoPlayPromise) {
      videoPlayPromise.then(() => {
        if (this.videoElement) {
          this.videoElement.pause()
          this.setState({ playing: false })
        }
      })
    }
  }

  scheduleVideoPlay () {
    this.discardScheduledVideoPlay()
    this.videoPlayTimeout = setTimeout(() => {
      this.playVideo()
    }, 500)
  }

  discardScheduledVideoPlay () {
    if (this.videoPlayTimeout) {
      clearTimeout(this.videoPlayTimeout)
      this.videoPlayTimeout = null
    }
  }

  onMouseOver () {
    this.discardVideoReset()
    this.scheduleVideoPlay()
  }

  onMouseOut () {
    this.discardScheduledVideoPlay()
    this.pauseVideo()
    this.scheduleVideoReset()
  }

  render () {
    const { item, onAddToFavourites, rank, onAddToCollections, onItemLinkClick, onAddToCart, muted } = this.props

    let videoLinkStyle
    let overlayStyle

    if (this.state.hideCursor) {
      videoLinkStyle = { cursor: 'none' }
      overlayStyle = { opacity: 0 }
    }

    const videoLength = undefsafe(item.attributes.find(attribute => attribute.name === 'length-video'), 'value')
    const videoUrl = undefsafe(item, 'previews.icon_with_video_preview.video_url')
    const posterUrl = undefsafe(item, 'previews.icon_with_video_preview.landscape_url')

    return (
      <div className={styles.container}>
        <div className={styles.videoContainer} onMouseOver={this.onMouseOver} onMouseOut={this.onMouseOut}>
          <a className={styles.videoLink}
            href={`${item.url}?s_rank=${rank}`}
            onClick={onItemLinkClick}
            style={videoLinkStyle}>
            <VideoLoader
              muted={muted}
              preload='none'
              onMouseOver={this.onMouseOverVideo}
              onMouseOut={this.onMouseOutVideo}
              onMouseMove={this.onMouseMoveVideo}
              videoRef={this.setVideoElementRef}
              className={styles.video}
              loop
              src={videoUrl}
              poster={posterUrl} />
          </a>

          <div className={styles.overlay} style={overlayStyle}>
            <div className={styles.itemActions}>
              <ItemActions
                item={item}
                defaultTheme={false}
                onAddToFavourites={onAddToFavourites}
                onAddToCollections={onAddToCollections}
                onAddToCart={onAddToCart} />
            </div>
            <div className={styles.price}>${item.price}</div>
          </div>
        </div>
        {item.trending && <TrendingSash />}
        <div className={styles.meta}>
          <div className={styles.titleContainer}>
            <a className={styles.title} href={`${item.url}?s_rank=${rank}`} onClick={onItemLinkClick}>{item.name}</a>
          </div>
          <div className={styles.duration}>{videoLength}</div>
        </div>
      </div>
    )
  }
}

export default ItemCardGridVideo



// WEBPACK FOOTER //
// ./client/components/itemCard/gridVideo.js